/**
 * Las piezas del reporte: los bloques numerados, las opciones, las estrellas y el dato.
 *
 * Salen de T7 y T8 del doc del 16-08. El reporte dejó de ser un formulario con campos
 * sueltos y pasó a ser una lista de bloques numerados donde CADA BLOQUE EMPIEZA POR LO
 * QUE YA SABEMOS y solo pregunta lo que no se puede saber (reglas 3 y 5 del doc). Eso es
 * lo que hay aquí: el sitio donde se pinta el dato, y el sitio donde se pregunta.
 *
 * Los textos no viven en estas piezas: los pone quien las usa, porque son los del doc y
 * tienen que poder leerse tal cual en el formulario, no repartidos por tres ficheros.
 */
import React, { useState } from 'react';
import { Star } from 'lucide-react';
import { CANTIDAD_COMIDA_VALOR } from '../../lib/respuestasDelCierre';
import { plural } from '../../lib/labels';

const ORANGE = '#FF671F';

/** Un bloque del reporte: "04 Tu dieta". El número lo pone el formulario, porque cambia
 *  de un plan a otro (el que no lleva lesiones ni cardio tiene 11 bloques, no 13). */
export const Bloque = ({ numero, titulo, sub, children, testid }) => (
    <div className="bg-card border border-border rounded-2xl p-4 space-y-3" data-testid={testid}>
        <div>
            <p className="text-sm font-bold text-foreground">
                {numero && <span className="text-muted-foreground tabular-nums mr-1.5">{numero}</span>}
                {titulo}
            </p>
            {sub && <p className="text-[13px] text-muted-foreground mt-0.5">{sub}</p>}
        </div>
        {children}
    </div>
);

/** Una pregunta con sus opciones cerradas. `columnas` para las listas cortas. */
export const Opciones = ({ pregunta, ayuda, opciones, valor, onChange, columnas = 1, testid }) => (
    <div data-testid={testid}>
        {pregunta && <p className="text-sm text-foreground mb-2">{pregunta}</p>}
        {ayuda && <p className="text-[13px] text-muted-foreground -mt-1 mb-2">{ayuda}</p>}
        <div className={`grid gap-2 ${columnas === 1 ? 'grid-cols-1' : columnas === 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-4'}`}>
            {opciones.map(o => {
                const activo = valor === o.value;
                return (
                    <button key={o.value} type="button" data-testid={`${testid}-${o.value}`}
                        onClick={() => onChange(activo ? '' : o.value)}
                        className={`py-2.5 px-3 rounded-xl border text-sm font-semibold text-left transition-all ${
                            activo ? 'border-[#FF671F] bg-[#FF671F]/10 text-[#FF671F]'
                                   : 'border-border bg-muted text-foreground/70 hover:border-foreground/30'}`}>
                        {o.label}
                    </button>
                );
            })}
        </div>
    </div>
);

/** Un campo libre. En el reporte casi todos son opcionales y se dice cuando no lo son. */
export const TextoLibre = ({ etiqueta, ayuda, valor, onChange, placeholder, filas = 3, testid }) => (
    <div>
        {etiqueta && <p className="text-sm text-foreground mb-1.5">{etiqueta}</p>}
        {ayuda && <p className="text-[13px] text-muted-foreground -mt-1 mb-1.5">{ayuda}</p>}
        <textarea
            value={valor || ''} rows={filas} placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)} data-testid={testid}
            className="w-full bg-muted border border-input rounded-xl px-3 py-2.5 text-foreground text-sm placeholder-foreground/20 focus:outline-none focus:border-[#FF671F] transition-colors resize-none"
        />
    </div>
);

/**
 * LAS SENSACIONES, CON ESTRELLAS ("y las sensaciones con estrellas, como pediste").
 *
 * Se puede desmarcar volviendo a pulsar la misma: una estrella puesta sin querer, en un
 * campo que no es obligatorio, no se puede quitar de ninguna otra forma.
 *
 * `minLabel` y `maxLabel` escriben los extremos debajo, como en las escalas del check-in
 * diario: sin ellos el cliente no sabe si cinco estrellas es ir de lujo o ir fatal.
 */
export const Estrellas = ({ valor, onChange, testid, soloLectura = false, minLabel, maxLabel }) => (
    <div className="w-fit" data-testid={testid}>
        <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map(n => {
                const puesta = (valor || 0) >= n;
                return (
                    <button key={n} type="button" disabled={soloLectura}
                        onClick={() => onChange(valor === n ? null : n)}
                        data-testid={`${testid}-${n}`} aria-label={`${n} de 5`}
                        className={`p-1 transition-transform ${soloLectura ? '' : 'hover:scale-110'}`}>
                        <Star className="w-7 h-7" style={{ color: puesta ? ORANGE : 'currentColor' }}
                            fill={puesta ? ORANGE : 'none'}
                            strokeWidth={puesta ? 0 : 1.5} />
                    </button>
                );
            })}
        </div>
        {(minLabel || maxLabel) && (
            <div className="flex justify-between mt-0.5 px-1">
                <span className="text-[11px] text-muted-foreground">{minLabel}</span>
                <span className="text-[11px] text-muted-foreground">{maxLabel}</span>
            </div>
        )}
    </div>
);

/** "★★★★☆" para leer, no para tocar: la media de lo que fue marcando cada día. */
export const EstrellasMedia = ({ valor }) => {
    const n = Math.round(Number(valor) || 0);
    return (
        <span className="tracking-widest" style={{ color: ORANGE }} aria-label={`${valor} de 5`}>
            {'★'.repeat(n)}{'☆'.repeat(Math.max(0, 5 - n))}
        </span>
    );
};

/** Los dos botones de una pregunta de sí o no, uno al lado del otro. */
export const DosBotones = ({ opciones, valor, onChange, testid }) => (
    <div className="grid grid-cols-2 gap-2">
        {opciones.map(o => {
            const activo = valor === o.value;
            return (
                <button key={o.value} type="button" data-testid={`${testid}-${o.value}`}
                    onClick={() => onChange(activo ? '' : o.value)}
                    className={`py-2.5 px-3 rounded-xl border text-sm font-semibold transition-all ${
                        activo ? 'border-[#FF671F] bg-[#FF671F]/10 text-[#FF671F]'
                               : 'border-border bg-muted text-foreground/70 hover:border-foreground/30'}`}>
                    {o.label}
                </button>
            );
        })}
    </div>
);

/** "el 8 y el 14 de agosto": una lista de cosas dicha como se dice hablando. */
export const enumerar = (lista) => {
    const xs = (lista || []).filter(Boolean);
    if (!xs.length) return '';
    if (xs.length === 1) return xs[0];
    return `${xs.slice(0, -1).join(', ')} y ${xs[xs.length - 1]}`;
};

/**
 * Lo mismo con fechas, sin repetir el mes: "el 8 y el 14 de agosto", no "el 8 de agosto
 * y el 14 de agosto". Si caen en meses distintos se dice el de cada una, que ahí sí hace
 * falta.
 */
export const enumerarFechas = (fechas) => {
    const xs = (fechas || []).filter(Boolean);
    if (xs.length < 2) return enumerar(xs);
    const mes = (f) => (String(f).match(/ de (\w+)$/) || [])[1];
    const meses = new Set(xs.map(mes));
    if (meses.size !== 1 || !mes(xs[0])) return enumerar(xs);
    const sinMes = xs.slice(0, -1).map(f => String(f).replace(/ de \w+$/, ''));
    return enumerar([...sinMes, xs[xs.length - 1]]);
};

/** El peso, con coma decimal: "77,1 kg". */
export const kg = (v) => (v == null ? null : `${String(v).replace('.', ',')} kg`);

/**
 * LA ESCALA DE 0 A 10, con los dos extremos escritos.
 *
 * Vivía dentro de `ReporteMensual.jsx` («¿el programa está cumpliendo tus expectativas?»)
 * y sube aquí porque el paso 2 del quincenal la usa dos veces con otras preguntas. Es la
 * misma escala de las maquetas: once casillas, la elegida en naranja, y debajo lo que
 * significan los dos bordes. Volver a pulsar la elegida la borra: un 0 es una respuesta y
 * tenía que haber forma de decir «no contesto».
 *
 * Las etiquetas NO se escriben aquí: son las del documento y cambian en cada pregunta.
 */
export const Escala0a10 = ({ pregunta, valor, onChange, minLabel, maxLabel, testid }) => (
    <div data-testid={testid}>
        {pregunta && <p className="text-sm text-foreground mb-2">{pregunta}</p>}
        <div className="flex gap-1">
            {Array.from({ length: 11 }, (_, n) => {
                const puesto = valor === n;
                return (
                    <button key={n} type="button" data-testid={`${testid}-${n}`}
                        onClick={() => onChange(puesto ? null : n)}
                        className={`flex-1 min-w-0 h-9 rounded-lg border text-[13px] font-bold tabular-nums transition-all ${
                            puesto ? 'border-[#FF671F] bg-[#FF671F] text-white'
                                   : 'border-border bg-muted text-foreground/60 hover:border-foreground/30'}`}>
                        {n}
                    </button>
                );
            })}
        </div>
        <div className="flex justify-between gap-4 mt-1.5">
            <span className="text-[11px] text-muted-foreground">{minLabel}</span>
            <span className="text-[11px] text-muted-foreground text-right">{maxLabel}</span>
        </div>
    </div>
);

/**
 * LAS TARJETAS DE LOS RESÚMENES DEL QUINCENAL Y DEL MENSUAL (24-10 y 25-10): un rótulo en
 * versalitas, filas de «etiqueta · valor» separadas por una línea, y el valor en gris cuando
 * no hay dato. Vivían en `QuincenalResumen`; el mensual del 25-10 pinta las mismas.
 */
export const TarjetaDelResumen = ({ titulo, children, testid }) => (
    <div className="rounded-2xl bg-card border border-border p-4 space-y-1" data-testid={testid}>
        {titulo && (
            <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground pb-1">
                {titulo}
            </p>
        )}
        {children}
    </div>
);

export const FilaDelResumen = ({ etiqueta, children, primera, testid }) => (
    <div data-testid={testid}
        className={`flex items-center justify-between gap-3 py-2.5 ${primera ? '' : 'border-t border-border'}`}>
        <span className="text-sm text-foreground/80 min-w-0">{etiqueta}</span>
        {children}
    </div>
);

export const ValorDelResumen = ({ fila }) => (
    <span className={`text-sm text-right tabular-nums ${
        fila.sin_dato ? 'text-muted-foreground' : 'font-bold text-foreground'}`}>
        {fila.valor}
    </span>
);

/** La etiqueta de una fila con su pie debajo en pequeño («jueves 8 de octubre»). */
const ConPie = ({ fila }) => (
    <span className="block">
        <span className="block">{fila.etiqueta}</span>
        {fila.pie && <span className="block text-[11px] text-muted-foreground">{fila.pie}</span>}
    </span>
);

/**
 * LAS FILAS DE «TU PESO» (artefacto del quincenal, 26-10; y del informe mensual, 27-10):
 * etiqueta con su pie, el peso y la diferencia con el de hoy. Las cuenta el servidor
 * (`core/quincenal.peso_del_quincenal`, `core/informe_con_checkin.peso_del_informe`).
 */
export const FilasDePeso = ({ filas, prefijo }) => (filas || []).map((f, i) => (
    <FilaDelResumen key={f.clave} primera={!i} testid={`${prefijo}-peso-${f.clave}`}
        etiqueta={<ConPie fila={f} />}>
        <span className="flex items-baseline gap-1.5">
            <ValorDelResumen fila={f} />
            {f.dif && <span className="text-[11px] text-muted-foreground tabular-nums">{f.dif}</span>}
        </span>
    </FilaDelResumen>
));

/**
 * LAS FILAS DE «CÓMO TE HAS SENTIDO» (`core/quincenal.filas_de_como_te_has_sentido`): la
 * media en estrellas y, en la saciedad, la respuesta más repetida y en cuántos días. Las
 * pintan el quincenal y el informe mensual, que dicen lo mismo.
 */
export const FilasDeSensaciones = ({ filas, prefijo }) => (filas || []).map((f, i) => (
    <FilaDelResumen key={f.clave} primera={!i} testid={`${prefijo}-sensacion-${f.clave}`}
        etiqueta={(
            <span className="block">
                <ConPie fila={f} />
                {f.respuesta && (
                    <span className="block mt-0.5 text-sm" data-testid={`${prefijo}-respuesta-${f.clave}`}>
                        {CANTIDAD_COMIDA_VALOR[f.respuesta]}
                    </span>
                )}
            </span>
        )}>
        {f.respuesta
            ? <span className="text-sm text-muted-foreground whitespace-nowrap">{f.veces} de {plural(f.de, 'día')}</span>
            : f.estrellas == null
            ? <span className="text-sm text-muted-foreground">Sin datos</span>
            : <span className="text-lg" data-testid={`${prefijo}-estrellas-${f.clave}`}><EstrellasMedia valor={f.estrellas} /></span>}
    </FilaDelResumen>
));

/**
 * UNA BARRA POR TRAMOS (artefacto del quincenal, 26-10): `de` tramos y los `hechos` primeros
 * en naranja. La usan la cabecera de las tres pantallas del quincenal (3 tramos) y las filas
 * de «Lo que has hecho» («9/11»). Con muchos tramos no se distinguen: a partir de 16, una
 * barra seguida con su proporción.
 */
export const BarraDeTramos = ({ hechos, de, testid }) => {
    const total = Math.max(0, Number(de) || 0);
    const llenos = Math.max(0, Math.min(total, Number(hechos) || 0));
    if (!total) return null;
    if (total > 15) {
        return (
            <div className="h-1 rounded-full bg-foreground/10 overflow-hidden" data-testid={testid}>
                <div className="h-full" style={{ width: `${(100 * llenos) / total}%`, backgroundColor: ORANGE }} />
            </div>
        );
    }
    return (
        <div className="flex gap-1" data-testid={testid}>
            {Array.from({ length: total }, (_, i) => (
                <span key={i} className={`h-1 flex-1 rounded-full ${i < llenos ? '' : 'bg-foreground/10'}`}
                    style={i < llenos ? { backgroundColor: ORANGE } : undefined} />
            ))}
        </div>
    );
};

/**
 * LA CABECERA DE LAS PANTALLAS DEL QUINCENAL (artefacto del 26-10):
 *
 *     INFORME QUINCENAL                       (antetítulo, en naranja)
 *     RESUMEN DE TUS ÚLTIMOS 11 REGISTROS
 *     Del sábado 26 de septiembre al martes 6 de octubre
 *     ━━━━━━━━  ────────  ────────            (paso 1 de 3)
 */
export const CabeceraDelQuincenal = ({ antetitulo, titulo, sub, paso, de = 3 }) => (
    <div className="space-y-1.5" data-testid="quincenal-cabecera">
        {antetitulo && (
            <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: ORANGE }}>
                {antetitulo}
            </p>
        )}
        {titulo && (
            <h2 className="text-2xl font-bold text-foreground uppercase leading-tight"
                data-testid="quincenal-cabecera-titulo"
                style={{ fontFamily: 'Barlow Condensed', letterSpacing: '0.02em' }}>
                {titulo}
            </h2>
        )}
        {sub && <p className="text-sm text-muted-foreground" data-testid="quincenal-cabecera-sub">{sub}</p>}
        <div className="pt-1.5"><BarraDeTramos hechos={paso} de={de} testid={`quincenal-paso-${paso}-de-${de}`} /></div>
    </div>
);

/**
 * EL «NO» Y EL «SÍ» EN PÍLDORA Y LA CASILLA DE BORDE DISCONTINUO que se abre con el sí, como
 * en las maquetas del quincenal (24-10) y del mensual (25-10). Vivían en `QuincenalPreguntas`.
 */
export const CasillaLibre = ({ valor, onChange, placeholder, testid }) => (
    <textarea value={valor} onChange={(e) => onChange(e.target.value)}
        rows={3} maxLength={4000} placeholder={placeholder}
        data-testid={testid}
        className="w-full rounded-xl border border-dashed border-foreground/25 bg-muted px-3 py-2.5 text-sm text-foreground placeholder-foreground/40 focus:outline-none focus:border-[#FF671F] resize-none" />
);

export const Pildora = ({ activo, onClick, children, testid }) => (
    <button type="button" onClick={onClick} data-testid={testid} aria-pressed={activo}
        className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${
            activo ? 'text-white border-transparent'
                : 'border-border bg-card text-foreground/80 hover:border-foreground/30'}`}
        style={activo ? { backgroundColor: ORANGE } : undefined}>
        {children}
    </button>
);

/**
 * UNA LISTA DE ETIQUETAS QUITABLES: las que ya dijo, más las que añada.
 *
 * Se usa dos veces, y las dos salen del documento del 1-09 con la misma forma: los
 * ejercicios que le dan molestias y las máquinas que no tiene. En los dos casos la lista
 * llega con lo de la última vez -- «Estos son los que me diste. Quita los que ya no y
 * añade los nuevos» -- y lo que se guarda es la lista entera, no lo que cambió.
 */
export const ListaDeEtiquetas = ({ lista, onChange, testid, etiqueta, ejemplo, anadir: textoAnadir,
                                   redondas = false }) => {
    // En píldora (`redondas`), como en la maqueta de «Tu material» del mensual (28-10).
    const forma = redondas ? 'rounded-full px-3.5 py-1.5 text-sm' : 'rounded-lg px-2.5 py-1 text-[13px]';
    const [nuevo, setNuevo] = useState('');
    const [escribiendo, setEscribiendo] = useState(false);

    const anadir = () => {
        const t = nuevo.trim();
        if (!t) { setEscribiendo(false); return; }
        onChange([...(lista || []), t]);
        setNuevo('');
        setEscribiendo(false);
    };

    return (
        <div data-testid={testid}>
            {etiqueta && <p className="text-[13px] text-muted-foreground mb-1.5">{etiqueta}</p>}
            <div className={`flex flex-wrap items-center ${redondas ? 'gap-2' : 'gap-1.5'}`}>
                {(lista || []).map((e, i) => (
                    <button key={`${e}-${i}`} type="button"
                        onClick={() => onChange(lista.filter((_, j) => j !== i))}
                        className={`${forma} bg-card border border-border text-foreground/80 hover:border-foreground/30`}
                        title="Quitar">
                        {e} <span className={redondas ? 'text-[#FF671F]' : 'text-muted-foreground'}>×</span>
                    </button>
                ))}
                {escribiendo ? (
                    <input autoFocus value={nuevo} onChange={(e) => setNuevo(e.target.value)}
                        onBlur={anadir}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); anadir(); } }}
                        data-testid={`${testid}-input`} placeholder={ejemplo}
                        className={`${forma} bg-card border border-input text-foreground outline-none focus:border-[#FF671F]`} />
                ) : (
                    <button type="button" onClick={() => setEscribiendo(true)} data-testid={`${testid}-anadir`}
                        className={`${forma} border ${redondas ? 'border-border' : 'border-dashed border-border'} text-muted-foreground hover:text-foreground`}>
                        {textoAnadir}
                    </button>
                )}
            </div>
        </div>
    );
};
